import clsx from 'clsx'
import parse from 'html-react-parser'
import { Link } from 'react-router'
import missingImg from '@/site/assets/launcher-icon-4x.png'
import { formatDuration, formatViews, joinMeta } from '@/site/lib/format'
import { hitImage, hitPath, hitTitle } from '@/site/lib/tutorialHit'
import type { TutorialHit } from '@/site/lib/types'

interface Props {
  hit: TutorialHit
  /** "stitched": bordered card with a dashed divider (home page sections); "plain": image + text. */
  variant?: 'plain' | 'stitched'
  badge?: string
  /** Show the view count (popularity rows). */
  showViews?: boolean
  /** Heading level of the title, to fit the page outline. */
  headingLevel?: 'h2' | 'h3'
}

/** A tutorial in a grid: thumbnail, title, duration (and views). */
export default function TutorialCard({ hit, variant = 'plain', badge, showViews = false, headingLevel: Heading = 'h3' }: Props) {
  const title = parse(hitTitle(hit).trim())
  const meta = joinMeta(formatDuration(hit.duration), showViews ? formatViews(hit.views) : '')
  const stitched = variant === 'stitched'

  return (
    <Link
      to={hitPath(hit)}
      className={clsx(
        'group flex flex-col',
        stitched ? 'overflow-hidden rounded-[20px] border-2 border-qt-line bg-white' : 'gap-2.5',
      )}
    >
      <div className="relative">
        <img
          loading="lazy"
          src={hitImage(hit) ?? missingImg}
          alt=""
          width="640"
          height="400"
          className={clsx('aspect-[16/10] w-full bg-qt-soft object-cover', !stitched && 'rounded-[18px]')}
          onError={(e) => {
            e.currentTarget.src = missingImg
          }}
        />
        {badge && (
          <span className="absolute top-3 left-3 rounded-lg bg-qt-badge px-2.5 py-1 text-xs font-extrabold text-qt-badge-ink">{badge}</span>
        )}
      </div>
      <div className={clsx('flex flex-col gap-1.5', stitched && 'border-t-[2.5px] border-dashed border-t-qt-stitch px-4 pt-4 pb-5')}>
        <Heading className="text-base leading-snug font-bold group-hover:underline">{title}</Heading>
        {meta && <p className="text-[13px] font-semibold text-qt-muted">{meta}</p>}
      </div>
    </Link>
  )
}
